import { Component, inject, OnDestroy, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { startWith, Subject, takeUntil } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TitleCasePipe } from '@angular/common';

@Component({
  selector: 'date-picker-header',
  styleUrl: './date-picker-header.scss',
  templateUrl: './date-picker-header.html',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatMenuModule, MatFormFieldModule, TitleCasePipe],
})
export class DatePickerHeader<D> implements OnDestroy {
  private _calendar = inject<MatCalendar<D>>(MatCalendar);
  private _dateAdapter = inject<DateAdapter<D>>(DateAdapter);
  private _dateFormats = inject(MAT_DATE_FORMATS);

  private _destroyed = new Subject<void>();

  readonly periodLabel = signal('');

  constructor() {
    this._calendar.stateChanges.pipe(startWith(null), takeUntil(this._destroyed)).subscribe(() => {
      this.periodLabel.set(
        this._dateAdapter
          .format(this._calendar.activeDate, this._dateFormats.display.monthYearLabel)
          .toLocaleUpperCase()
      );
    });
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  onMonthMenuOpened() {
    setTimeout(() => {
      const panel = document.querySelector('.mat-mdc-menu-panel.month-menu');
      const active = document.querySelector(
        '.mat-mdc-menu-panel.month-menu .mat-mdc-menu-item.active'
      );

      if (panel && active) {
        (active as HTMLElement).scrollIntoView({ block: 'center', behavior: 'auto' });
      }
    }, 0);
  }

  onYearMenuOpened() {
    setTimeout(() => {
      const panel = document.querySelector('.mat-mdc-menu-panel.year-menu');
      const active = document.querySelector(
        '.mat-mdc-menu-panel.year-menu .mat-mdc-menu-item.active'
      );
      if (panel && active) {
        (active as HTMLElement).scrollIntoView({ block: 'center', behavior: 'auto' });
      }
    }, 0);
  }

  getYears(): number[] {
    const currentYear = this._dateAdapter.getYear(this._calendar.activeDate);
    const years: number[] = [];
    for (let i = currentYear - 100; i <= currentYear + 100; i++) {
      years.push(i);
    }
    return years;
  }

  getMonths(): string[] {
    return this._dateAdapter.getMonthNames('long');
  }

  setMonth(monthIndex: number) {
    const activeDate = this._calendar.activeDate;
    const year = this._dateAdapter.getYear(activeDate);
    const day = Math.min(
      this._dateAdapter.getDate(activeDate),
      this._dateAdapter.getNumDaysInMonth(this._dateAdapter.createDate(year, monthIndex, 1))
    );
    this._calendar.activeDate = this._dateAdapter.createDate(year, monthIndex, day);
  }

  setYear(year: number) {
    const activeDate = this._calendar.activeDate;
    const month = this._dateAdapter.getMonth(activeDate);
    const day = Math.min(
      this._dateAdapter.getDate(activeDate),
      this._dateAdapter.getNumDaysInMonth(this._dateAdapter.createDate(year, month, 1))
    );
    this._calendar.activeDate = this._dateAdapter.createDate(year, month, day);
  }

  getActiveMonth(): number {
    return this._dateAdapter.getMonth(this._calendar.activeDate);
  }

  getActiveYear(): number {
    return this._dateAdapter.getYear(this._calendar.activeDate);
  }
}
